// Variables for each client tab's list, shared by the route loader that fetches
// the first page and the hook that renders it. The two have to agree exactly:
// the hook reads the loader's result from the cache, and any difference is a
// cache miss that fetches the same page twice.

import {
  CaraWorkspaceSortField,
  ClientDocumentSortField,
  SortDirection,
  type CaraWorkspaceSortInput,
  type ClientArtifactFilterInput,
  type ClientDocumentSortInput,
  type ClientFileFilterInput,
  type GetClientArtifactsQueryVariables,
  type GetClientFilesQueryVariables,
  type SearchCaraWorkspacesFilterInput,
  type SearchCaraWorkspacesQueryVariables
} from '~/api/graphql';

/** A client with hundreds of linked workspaces is already ordinary. */
export const CLIENT_WORKSPACES_PAGE_SIZE = 25;
export const CLIENT_DOCUMENTS_PAGE_SIZE = 50;

export const DEFAULT_CLIENT_WORKSPACE_SORT: CaraWorkspaceSortInput = {
  field: CaraWorkspaceSortField.UpdatedAt,
  direction: SortDirection.Desc
};

export const DEFAULT_CLIENT_DOCUMENT_SORT: ClientDocumentSortInput = {
  field: ClientDocumentSortField.CreatedAt,
  direction: SortDirection.Desc
};

export const clientWorkspacesVariables = (
  clientId: string,
  filter?: SearchCaraWorkspacesFilterInput,
  sort: CaraWorkspaceSortInput = DEFAULT_CLIENT_WORKSPACE_SORT
): SearchCaraWorkspacesQueryVariables => ({
  first: CLIENT_WORKSPACES_PAGE_SIZE,
  filter: { ...filter, clientId },
  sort
});

export const clientArtifactsVariables = (
  clientId: string,
  filter?: ClientArtifactFilterInput,
  sort: ClientDocumentSortInput = DEFAULT_CLIENT_DOCUMENT_SORT
): GetClientArtifactsQueryVariables => ({
  id: clientId,
  first: CLIENT_DOCUMENTS_PAGE_SIZE,
  filter,
  sort
});

export const clientFilesVariables = (
  clientId: string,
  filter?: ClientFileFilterInput,
  sort: ClientDocumentSortInput = DEFAULT_CLIENT_DOCUMENT_SORT
): GetClientFilesQueryVariables => ({
  id: clientId,
  first: CLIENT_DOCUMENTS_PAGE_SIZE,
  filter,
  sort
});

/**
 * The activity timeline pages each source newest-created first, in steps small
 * enough that the first screen does not wait on four full lists.
 */
export const CLIENT_ACTIVITY_PAGE_SIZE = 10;

export const clientActivityWorkspacesVariables = (
  clientId: string
): SearchCaraWorkspacesQueryVariables => ({
  first: CLIENT_ACTIVITY_PAGE_SIZE,
  filter: { clientId },
  sort: { field: CaraWorkspaceSortField.CreatedAt, direction: SortDirection.Desc }
});

export const clientActivityArtifactsVariables = (
  clientId: string
): GetClientArtifactsQueryVariables => ({
  id: clientId,
  first: CLIENT_ACTIVITY_PAGE_SIZE,
  sort: DEFAULT_CLIENT_DOCUMENT_SORT
});

export const clientActivityFilesVariables = (clientId: string): GetClientFilesQueryVariables => ({
  id: clientId,
  first: CLIENT_ACTIVITY_PAGE_SIZE,
  sort: DEFAULT_CLIENT_DOCUMENT_SORT
});
